/**
 * KioskCart - Shopping cart view
 */
import { ChevronLeft, ShoppingBag, Plus, Minus, Trash2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useKiosk } from './KioskContext';
import KioskCoffeeClubRedemption from './KioskCoffeeClubRedemption';

export default function KioskCart() {
  const {
    setView, isAccessible, buttonSizeClass, iconClass, iconButtonClass, largeIconClass,
    cart, cartTotal, vatRate, vatAmount, finalTotal, updateQuantity, removeFromCart,
    customer, coffeeClubStatus, handleAddCoffeeClubRedemption
  } = useKiosk();

  return (
    <div className="min-h-screen bg-gray-950 text-white p-8" data-testid="kiosk-cart">
      <Button 
        variant="ghost" 
        onClick={() => setView('ordering')}
        className={`mb-8 text-white ${isAccessible ? 'px-6 py-4 text-xl' : 'px-5 py-3 text-lg'} active:bg-gray-800 touch-manipulation`}
        style={{ touchAction: 'manipulation' }}
      >
        <ChevronLeft className={isAccessible ? 'w-8 h-8' : 'w-6 h-6'} />
        <span className="ml-2">Continue Shopping</span>
      </Button>

      <div className="max-w-4xl mx-auto">
        <h1 className={`${isAccessible ? 'text-5xl' : 'text-4xl'} font-bold mb-8`}>Your Order</h1>

        {/* Coffee Club Redemption Banner */}
        {customer && coffeeClubStatus?.has_active_pass && (
          <div className="mb-6">
            <KioskCoffeeClubRedemption 
              onAddFreeCoffee={handleAddCoffeeClubRedemption}
            />
          </div>
        )}

        {cart.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingBag className={`${isAccessible ? 'w-32 h-32' : 'w-24 h-24'} mx-auto mb-4 text-gray-600`} />
            <p className={`${isAccessible ? 'text-3xl' : 'text-2xl'} text-gray-400`}>Your cart is empty</p>
          </div>
        ) : (
          <>
            <div className="space-y-4 mb-8">
              {cart.map((item, idx) => (
                <div key={`cart-${idx}`} className={`bg-gray-900 rounded-2xl ${isAccessible ? 'p-8' : 'p-6'} flex items-center gap-6`}>
                  {item.image && (
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className={`${isAccessible ? 'w-32 h-32' : 'w-24 h-24'} rounded-xl object-cover`}
                    />
                  )}
                  <div className="flex-1">
                    <h3 className={`${isAccessible ? 'text-3xl' : 'text-2xl'} font-medium`}>{item.name}</h3>
                    {item.selectedSize && (
                      <p className="text-gray-500 text-sm">{item.selectedSize}</p>
                    )}
                    {item.modifiers?.length > 0 && (
                      <p className="text-blue-400 text-sm">
                        {item.modifiers.map(m => m.selected_option || m.name).join(', ')}
                      </p>
                    )}
                    {item.removedIngredients?.length > 0 && (
                      <p className="text-red-400 text-sm">No: {item.removedIngredients.join(', ')}</p>
                    )}
                    {item.extraIngredients?.length > 0 && (
                      <p className="text-green-400 text-sm">Extra: {item.extraIngredients.map(e => e.name).join(', ')}</p>
                    )}
                    <p className={`${isAccessible ? 'text-2xl' : 'text-xl'} text-gray-400`}>{item.price.toFixed(2)} each</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => updateQuantity(idx, -1)}
                      className={`${isAccessible ? 'w-16 h-16' : 'w-14 h-14'} rounded-full border-gray-600 text-white active:scale-95 active:bg-gray-600 touch-manipulation`}
                      style={{ touchAction: 'manipulation' }}
                    >
                      <Minus className={isAccessible ? 'w-8 h-8' : 'w-6 h-6'} />
                    </Button>
                    <span className={`${isAccessible ? 'text-4xl' : 'text-3xl'} font-bold w-16 text-center`}>{item.quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => updateQuantity(idx, 1)}
                      className={`${isAccessible ? 'w-16 h-16' : 'w-14 h-14'} rounded-full border-gray-600 text-white active:scale-95 active:bg-gray-600 touch-manipulation`}
                      style={{ touchAction: 'manipulation' }}
                    >
                      <Plus className={isAccessible ? 'w-8 h-8' : 'w-6 h-6'} />
                    </Button>
                  </div>
                  <div className="text-right min-w-[120px]">
                    <div className={`${isAccessible ? 'text-3xl' : 'text-2xl'} font-bold`}>{(item.price * item.quantity).toFixed(2)}</div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFromCart(idx)}
                    className={`text-red-400 hover:text-red-300 active:bg-red-900/30 ${isAccessible ? 'w-16 h-16' : 'w-14 h-14'} touch-manipulation`}
                    style={{ touchAction: 'manipulation' }}
                  >
                    <Trash2 className={isAccessible ? 'w-10 h-10' : 'w-8 h-8'} />
                  </Button>
                </div>
              ))}
            </div>

            <div className={`bg-gray-900 rounded-2xl ${isAccessible ? 'p-8' : 'p-6'} mb-8`}>
              <div className="space-y-3">
                <div className={`flex justify-between ${isAccessible ? 'text-2xl' : 'text-xl'}`}>
                  <span>Subtotal</span>
                  <span>{cartTotal.toFixed(2)}</span>
                </div>
                <div className={`flex justify-between ${isAccessible ? 'text-xl' : 'text-lg'} text-gray-400`}>
                  <span>12.5% VAT Included</span>
                  <span>(${vatAmount.toFixed(2)})</span>
                </div>
                <div className={`flex justify-between ${isAccessible ? 'text-4xl' : 'text-3xl'} font-bold pt-3 border-t border-gray-700`}>
                  <span>Total</span>
                  <span className="text-green-400">{finalTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <Button 
              onClick={() => setView('checkout')}
              className={`w-full ${isAccessible ? 'py-12 text-4xl' : 'py-10 text-3xl'} bg-primary hover:bg-primary/90 active:bg-primary/80 rounded-2xl touch-manipulation`}
              style={{ touchAction: 'manipulation' }}
            >
              Proceed to Checkout
              <ArrowRight className={`${isAccessible ? 'w-12 h-12' : 'w-10 h-10'} ml-4`} />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
